import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Menu, X, Star, Wrench, MapPin, CalendarDays, Search, User as UserIcon, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useStates, useAllGames } from "@/hooks/use-query-hooks";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import logo from "@/assets/usa-lottery-club-logo-sm.webp";

function MoneyBagIcon({ className = "" }: { className?: string }) {
  return (
    <span
      className={`material-symbols-outlined ${className}`}
      style={{ fontSize: "20px", lineHeight: 1 }}
      aria-hidden="true"
    >
      money_bag
    </span>
  );
}

export function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data: states } = useStates();
  const { data: allGames } = useAllGames();
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileQuery, setMobileQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  async function handleSignOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/", replace: true });
  }

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !allGames) return [] as any[];
    return (allGames as any[])
      .filter((g) => g.name?.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, allGames]);

  const mobileResults = useMemo(() => {
    const q = mobileQuery.trim().toLowerCase();
    if (!q || !allGames) return [] as any[];
    return (allGames as any[])
      .filter((g) => g.name?.toLowerCase().includes(q))
      .slice(0, 8);
  }, [mobileQuery, allGames]);

  const goToGame = (g: any) => {
    setSearchOpen(false);
    setQuery("");
    setMobileQuery("");
    setMobileOpen(false);
    if (g?.states?.slug) {
      navigate({ to: "/$stateSlug/$gameSlug", params: { stateSlug: g.states.slug, gameSlug: g.slug } });
    } else if (g?.slug) {
      navigate({ to: "/games/$gameSlug", params: { gameSlug: g.slug } });
    }
  };

  const navLinks = [
    { to: "/", label: "Jackpots", icon: MoneyBagIcon },
    { to: "/results", label: "Results", icon: CalendarDays },
    { to: "/states", label: "States", icon: MapPin },
    { to: "/tools", label: "Tools", icon: Wrench },
    { to: "/my-picks", label: "My Picks", icon: Star },
  ] as const;

  return (
    <header
      role="banner"
      className="sticky top-0 z-50 w-full border-b bg-[var(--mobile-nav-bg)] lg:bg-card/95 lg:backdrop-blur lg:supports-[backdrop-filter]:bg-card/80 [contain:layout_paint]"
    >

      <div className="container flex h-14 items-center gap-2 sm:gap-4 overflow-x-clip">
        <Link to="/" className="shrink-0" aria-label="USA Lottery Club home">
          <img
            src={logo}
            alt="USA Lottery Club"
            className="h-11 w-auto"
            width={170}
            height={44}
            fetchPriority="high"
            decoding="async"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-auto">
          {navLinks.map(({ to, label, icon: Icon }) => (
            <Button
              key={to}
              variant={location.pathname === to ? "secondary" : "ghost"}
              size="sm"
              asChild
              className={location.pathname === to ? "bg-accent text-accent-foreground hover:bg-accent/80" : ""}
            >
              <Link to={to} className="text-[14px]">
                <Icon className="h-4 w-4" />
                {label}
              </Link>
            </Button>
          ))}

          <Popover open={searchOpen && results.length > 0} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
              <div className="relative ml-2 w-64">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  aria-label="Search games"
                  placeholder="Search games..."
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setSearchOpen(true); }}
                  onFocus={() => setSearchOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && results[0]) {
                      e.preventDefault();
                      goToGame(results[0]);
                    }
                  }}
                  className="h-8 py-0 pl-9 text-sm leading-none bg-muted/60"
                />
              </div>
            </PopoverTrigger>
            <PopoverContent
              className="w-[--radix-popover-trigger-width] p-1"
              align="start"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <div className="max-h-72 overflow-y-auto">
                {results.map((g: any) => (
                  <button
                    key={g.id}
                    onClick={() => goToGame(g)}
                    className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent text-left"
                  >
                    {g.name}{g.states ? ` (${g.states.abbreviation})` : ""}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Account menu" className="ml-1">
                  <UserIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="truncate max-w-[220px]">{user.email ?? "Account"}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to="/my-picks">My Picks</Link></DropdownMenuItem>
                <DropdownMenuItem onClick={handleSignOut}><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </nav>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild className="lg:hidden ml-auto">
            <Button
              variant="ghost"
              size="icon"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav-sheet"
              aria-haspopup="dialog"
              className="min-h-11 min-w-11"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </SheetTrigger>
          <SheetContent
            id="mobile-nav-sheet"
            side="right"
            className="w-[88vw] max-w-sm sm:w-80 flex flex-col gap-4 overflow-y-auto bg-[var(--mobile-nav-bg)]"
          >

            <SheetTitle className="mb-4">
              <img src={logo} alt="USA Lottery Club" className="h-10 w-auto" />
            </SheetTitle>
            <div className="relative">
              <label htmlFor="mobile-search" className="sr-only">Search games</label>
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="mobile-search"
                type="search"
                placeholder="Search games..."
                value={mobileQuery}
                onChange={(e) => setMobileQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && mobileResults[0]) {
                    e.preventDefault();
                    goToGame(mobileResults[0]);
                  }
                }}
                className="h-11 w-full pl-9 text-base bg-muted/60"
                aria-label="Search games"
                aria-autocomplete="list"
                aria-controls="mobile-search-results"
                aria-expanded={mobileResults.length > 0}
              />
              {mobileResults.length > 0 && (
                <ul
                  id="mobile-search-results"
                  role="listbox"
                  className="mt-2 max-h-60 overflow-y-auto rounded-md border bg-popover p-1"
                >
                  {mobileResults.map((g: any) => (
                    <li key={g.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected="false"
                        onClick={() => goToGame(g)}
                        className="flex min-h-11 w-full items-center rounded-sm px-3 py-2 text-sm text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none break-words"
                      >
                        {g.name}{g.states ? ` (${g.states.abbreviation})` : ""}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <nav aria-label="Mobile" className="flex flex-col gap-1">
              {navLinks.map(({ to, label, icon: Icon }) => (
                <Button
                  key={to}
                  variant={location.pathname === to ? "secondary" : "ghost"}
                  className={`justify-start min-h-11 text-base hover:bg-accent focus-visible:bg-accent active:bg-accent/80 ${location.pathname === to ? "bg-accent text-accent-foreground" : ""}`}
                  asChild
                  onClick={() => setMobileOpen(false)}
                >
                  <Link to={to} aria-current={location.pathname === to ? "page" : undefined}>
                    <Icon className="h-4 w-4 mr-2" />
                    {label}
                  </Link>
                </Button>
              ))}

            </nav>
            <div>
              <Select
                onValueChange={(val) => {
                  navigate({ to: "/states/$stateSlug", params: { stateSlug: val } });
                  setMobileOpen(false);
                }}
              >
                <SelectTrigger className="w-full min-h-11" aria-label="Select state">
                  <SelectValue placeholder="Select State" />
                </SelectTrigger>
                <SelectContent>
                  {states?.map((s) => (
                    <SelectItem key={s.id} value={s.slug}>
                      {s.abbreviation} — {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}